import { useState } from "react";
import { User } from "lucide-react";

/**
 * Avatar for human chat messages. Renders the user's Microsoft 365 profile
 * photo (served by the platform's photo proxy) and falls back through
 * coloured initials → generic User icon if the photo is missing or fails to
 * load. The proxy decides where the image actually comes from (cached data
 * URI or live Graph fetch) — this component just trusts the URL.
 */

const PALETTE = [
  "bg-rose-500",
  "bg-orange-500",
  "bg-amber-500",
  "bg-emerald-500",
  "bg-teal-500",
  "bg-sky-500",
  "bg-indigo-500",
  "bg-violet-500",
  "bg-fuchsia-500",
];

function hashToPalette(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) | 0;
  }
  return PALETTE[Math.abs(h) % PALETTE.length];
}

function getInitials(name?: string | null): string {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function UserChatAvatar({
  userId,
  displayName,
  size = 28,
}: {
  userId?: string | null;
  displayName?: string | null;
  size?: number;
}) {
  const [photoFailed, setPhotoFailed] = useState(false);
  const initials = getInitials(displayName);
  const sizeClass = `h-[${size}px] w-[${size}px]`;
  const dim = { width: size, height: size, minWidth: size, minHeight: size };

  // No userId → never going to find a photo, render initials/icon directly.
  const showPhoto = !!userId && !photoFailed;

  if (showPhoto) {
    return (
      <img
        src={`/api/users/${userId}/photo`}
        alt={displayName || "User"}
        onError={() => setPhotoFailed(true)}
        className={`shrink-0 rounded-full object-cover ring-1 ring-border ${sizeClass}`}
        style={dim}
      />
    );
  }

  if (initials) {
    const palette = hashToPalette(userId || displayName || "user");
    return (
      <div
        className={`flex shrink-0 items-center justify-center rounded-full text-white font-medium ${palette}`}
        style={{ ...dim, fontSize: Math.max(10, Math.floor(size * 0.4)) }}
        aria-label={displayName || "User"}
      >
        {initials}
      </div>
    );
  }

  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-full bg-accent/10"
      style={dim}
      aria-label="User"
    >
      <User size={Math.floor(size * 0.5)} className="text-accent" />
    </div>
  );
}
